import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { PartnerForm } from "../components/PartnerForm";
import { BenefitLogo } from "../components/AdminBenefits";
import { ConfirmDialog } from "../components/ExpiredAccounts";
import { brl } from "../components/PurchaseDialog";
import { Plus, Pencil, Trash2, Store } from "lucide-react";

const STATUS_CLS = { approved: "text-emerald-500 border-emerald-500/40", pending: "text-amber-500 border-amber-500/40", rejected: "text-rose-500 border-rose-500/40" };

const PartnerRow = ({ b, onEdit, onDelete }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-border" data-testid={`my-partner-${b.id}`}>
    <BenefitLogo url={b.pending_logo_url || b.logo_url} name={b.name} className="h-14 w-14" />
    <div className="flex-1 min-w-0">
      <div className="font-medium flex flex-wrap items-center gap-2">
        {b.name}
        <Badge variant="outline" className={STATUS_CLS[b.status]} data-testid={`my-partner-status-${b.id}`}>{b.status_label}</Badge>
        {b.has_pending_changes && <Badge variant="outline" className={STATUS_CLS.pending}>Edição aguardando aprovação</Badge>}
      </div>
      <div className="text-xs text-muted-foreground">{b.category} · De {brl(b.original_price)} por {brl(b.price)}</div>
      {b.reject_reason && <div className="text-xs text-rose-500 mt-1" data-testid={`my-partner-reason-${b.id}`}>Motivo: {b.reject_reason}</div>}
    </div>
    <div className="flex gap-2 shrink-0">
      <Button size="sm" variant="outline" onClick={() => onEdit(b)} data-testid={`my-partner-edit-${b.id}`}><Pencil className="h-3 w-3 mr-1" /> Editar</Button>
      <Button size="sm" variant="outline" className="text-rose-500" onClick={() => onDelete(b)} data-testid={`my-partner-delete-${b.id}`}><Trash2 className="h-3 w-3" /></Button>
    </div>
  </div>
);

export default function MyPartners() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const load = useCallback(() => { api.get("/my/partners").then((r) => setList(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try { await api.delete(`/my/partners/${removing.id}`); toast.success("Removido"); setRemoving(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-24 pb-16 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Empresas parceiras</div>
        <div className="flex flex-wrap items-end justify-between gap-3 mt-1 mb-6">
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Minhas <span className="gold-text">empresas</span></h1>
          <Button className="gold-btn" onClick={() => setEditing({})} data-testid="my-partner-new-btn"><Plus className="h-4 w-4 mr-2" /> Cadastrar empresa</Button>
        </div>
        <Card className="p-4 mb-6 text-xs text-muted-foreground" data-testid="my-partners-rules">
          Cadastros e edições passam pela aprovação do administrador antes de aparecer na vitrine (a versão publicada continua no ar até a edição ser aprovada).
          Quando alguém compra, o valor fica <b className="text-foreground">em análise</b> até o comprador confirmar o recebimento; depois entra no seu saldo disponível, descontada a comissão da Conecta Mais. Os pedidos são atendidos pelo administrador.
        </Card>
        {!list ? <p className="text-muted-foreground">Carregando...</p> : list.length === 0 ? (
          <Card className="p-10 text-center" data-testid="my-partners-empty"><Store className="h-10 w-10 mx-auto text-amber-400" />
            <p className="mt-3 text-sm text-muted-foreground">Você ainda não cadastrou nenhuma empresa.</p></Card>
        ) : <div className="space-y-3">{list.map((b) => <PartnerRow key={b.id} b={b} onEdit={setEditing} onDelete={setRemoving} />)}</div>}
      </div>
      <PartnerForm editing={editing} onClose={() => setEditing(null)} onSaved={load} base="/my/partners" />
      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} testid="my-partner-delete-dialog"
        title="Remover empresa?" description={removing ? `${removing.name} sairá da vitrine.` : ""} onConfirm={remove} />
    </div>
  );
}
